import { PageTitle } from "components/PageTitle/PageTitle";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import {
  type DeliverySeries,
  type DeliverySeriesStatus,
  useDeleteDeliverySeries,
  useDeliverySeriesList,
} from "hooks/use-delivery-series-api";
import { cn } from "lib/utils";
import { Megaphone, MoreHorizontal, Plus } from "lucide-react";
import { type FC, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { DeliverySeriesFormModal } from "routes/DeliverySeriesPage/components/DeliverySeriesFormModal";
import { DeliverySeriesStatusBadge } from "routes/DeliverySeriesPage/components/DeliverySeriesStatusBadge";
import { formatShortDateTime } from "utils/relative-time";
import { toastError } from "utils/toast-utils";

/**
 * 配信シリーズ一覧ページ.
 * @return {JSX.Element} ページ
 */
export const DeliverySeriesListPage: FC = () => {
  const [statusFilter, setStatusFilter] = useState<
    DeliverySeriesStatus | "all"
  >("active");
  const status = statusFilter === "all" ? null : statusFilter;
  const { items, loading, refetch } = useDeliverySeriesList({ status });
  const { remove, loading: deleting } = useDeleteDeliverySeries();
  const [editing, setEditing] = useState<DeliverySeries | null>(null);
  const [creating, setCreating] = useState(false);

  /**
   * シリーズを削除する
   * @param {DeliverySeries} series 対象シリーズ
   * @return {Promise<void>}
   */
  const handleDelete = useCallback(
    async (series: DeliverySeries) => {
      const ok = window.confirm(
        `シリーズ「${series.name}」を削除しますか?\n配下の配信は単発配信に降格されます (削除はされません)。`,
      );
      if (!ok) return;
      try {
        await remove(series.id);
        toast.success("シリーズを削除しました");
        await refetch();
      } catch {
        toastError("シリーズの削除に失敗しました");
      }
    },
    [remove, refetch],
  );

  return (
    <div className="space-y-6 p-6 max-w-5xl">
      <header className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Megaphone className="text-primary" aria-hidden />
            <PageTitle className="text-2xl font-bold">配信シリーズ</PageTitle>
          </div>
          <Button type="button" size="sm" onClick={() => setCreating(true)}>
            <span className="inline-flex items-center gap-1">
              <Plus size={14} aria-hidden />
              新規シリーズ
            </span>
          </Button>
        </div>
        <p className="text-sm text-base-content/70">
          毎日繰り返し送る配信を意図単位で束ねるシリーズを管理します。
          単発配信はシリーズに紐付けず登録できます。
        </p>
      </header>

      <StatusTabs status={statusFilter} onChange={setStatusFilter} />

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-10">
            <Loading />
          </div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-sm text-base-content/60">
            シリーズはまだ登録されていません
          </div>
        ) : (
          <ul>
            {items.map(series => (
              <SeriesRow
                key={series.id}
                series={series}
                onEdit={() => setEditing(series)}
                onDelete={() => handleDelete(series)}
                disabled={deleting}
              />
            ))}
          </ul>
        )}
      </div>

      <DeliverySeriesFormModal
        isOpen={creating}
        mode="create"
        onClose={() => setCreating(false)}
        onSaved={() => {
          setCreating(false);
          refetch();
        }}
      />
      <DeliverySeriesFormModal
        isOpen={editing !== null}
        mode="edit"
        series={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          refetch();
        }}
      />
    </div>
  );
};

// ============================================================
// Sub-components
// ============================================================

type StatusTabsProps = {
  status: DeliverySeriesStatus | "all";
  onChange: (next: DeliverySeriesStatus | "all") => void;
};

/**
 * ステータス絞込タブ (snippets 現状タブ準拠の下線型).
 * @param {StatusTabsProps} props 値とハンドラ
 * @return {JSX.Element} タブ
 */
const StatusTabs: FC<StatusTabsProps> = ({ status, onChange }) => {
  const options: ReadonlyArray<{
    value: DeliverySeriesStatus | "all";
    label: string;
  }> = [
    { value: "active", label: "Active" },
    { value: "archived", label: "Archived" },
    { value: "all", label: "全て" },
  ];
  return (
    <div className="flex gap-6 border-b border-slate-200">
      {options.map(opt => {
        const isActive = opt.value === status;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              "relative px-2 py-2 text-sm font-medium transition-colors",
              isActive
                ? "text-primary"
                : "text-base-content/60 hover:text-base-content",
            )}
          >
            {opt.label}
            {isActive && (
              <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-primary" />
            )}
          </button>
        );
      })}
    </div>
  );
};

type SeriesRowProps = {
  series: DeliverySeries;
  onEdit: () => void;
  onDelete: () => void;
  disabled?: boolean;
};

/**
 * 一覧の 1 行. シリーズ名 + ステータス + 集計バッジ + メニュー.
 * @param {SeriesRowProps} props 行データ
 * @return {JSX.Element} 1 行
 */
const SeriesRow: FC<SeriesRowProps> = ({
  series,
  onEdit,
  onDelete,
  disabled,
}) => {
  const lastSent = series.stats.lastSentAt;
  const responseRate = Math.round(series.stats.responseRate * 100);
  return (
    <li className="px-4 py-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <Link
            to={`/delivery-series/${series.id}`}
            className="text-base font-semibold text-base-content hover:text-primary inline-flex items-center gap-2"
          >
            {series.name}
            <DeliverySeriesStatusBadge status={series.status} />
          </Link>
          <div className="mt-1 text-xs text-base-content/60 flex flex-wrap gap-x-4 gap-y-1">
            <span>配信 {series.stats.deliveryCount} 件</span>
            <span>
              リプライ {series.stats.totalReplies} 件 (未対応{" "}
              {series.stats.unresponded})
            </span>
            <span>対応率 {responseRate}%</span>
            {lastSent && <span>最終配信 {formatShortDateTime(lastSent)}</span>}
          </div>
          {(series.talentName || series.focusMatterTitle) && (
            <div className="mt-1 text-xs text-violet-700">
              {series.talentName && <span>👤 {series.talentName}</span>}
              {series.focusMatterTitle && (
                <span className="ml-3">📁 {series.focusMatterTitle}</span>
              )}
            </div>
          )}
        </div>
        <RowMenu onEdit={onEdit} onDelete={onDelete} disabled={disabled} />
      </div>
    </li>
  );
};

type RowMenuProps = {
  onEdit: () => void;
  onDelete: () => void;
  disabled?: boolean;
};

/**
 * 1 行右端の編集 / 削除メニュー (簡易ドロップダウン).
 * @param {RowMenuProps} props ハンドラ
 * @return {JSX.Element} メニュー
 */
const RowMenu: FC<RowMenuProps> = ({ onEdit, onDelete, disabled }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        className="p-1.5 rounded hover:bg-slate-100"
        onClick={() => setOpen(prev => !prev)}
        aria-label="メニュー"
      >
        <MoreHorizontal size={16} className="text-base-content/60" />
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-label="閉じる"
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 top-full mt-1 z-20 w-32 bg-white border border-slate-200 rounded-md shadow-md text-sm">
            <button
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-slate-50 text-base-content"
              onClick={() => {
                setOpen(false);
                onEdit();
              }}
              disabled={disabled}
            >
              編集
            </button>
            <button
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-slate-50 text-rose-600"
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
              disabled={disabled}
            >
              削除
            </button>
          </div>
        </>
      )}
    </div>
  );
};
